/**
 * What a mail IS, for the filter chips on the Mail screen (Akhil, 2026-09-19: "tags like quoted,
 * supplier reply, sending RFQ, enquiry created — so the purchaser can sort the mail").
 *
 * One kind per mail, decided from what the app already knows and in this order:
 *
 *   supplier-reply   a supplier answering one of OUR RFQs — the `[REQ-…]` tag we put in the
 *                    subject (`readsAsSupplierReply`, corrections included)
 *   rfq-sent         our own outgoing RFQ or reminder — the sent folder, our subject tag
 *   enquiry          an enquiry was made from this mail (any stage). There is no "quoted" kind
 *                    (Akhil, 2026-09-19): a quotation mail leaves no trace in the mailbox —
 *                    `send_quotation` goes out through Email Queue and files no Communication,
 *                    so the Sent folder never shows one (measured 2026-09-19, none on the bench)
 *   to-create        reads as an RFQ (classifier or a correction) and has no enquiry yet
 *   other            everything else
 */

import { classifyRfqMail, isOwnOutgoingRfq, repliedRfqId } from './mailClassify'
import { effectiveVerdict, readsAsSupplierReply } from './mailVerdictOverride'
import type { EnquirySummary, MailMessage } from './types'

export type MailKind = 'to-create' | 'auto-created' | 'enquiry' | 'rfq-sent' | 'supplier-reply' | 'other'

export const MAIL_KINDS: Record<MailKind, { label: string; hint: string }> = {
  'to-create': { label: 'To create', hint: 'Reads as a customer enquiry and has no enquiry yet' },
  'auto-created': { label: 'Auto-created', hint: 'Bosun made the enquiry from this mail without a person — open it and check the header' },
  enquiry: { label: 'Enquiry created', hint: 'An enquiry was made from this mail' },
  'rfq-sent': { label: 'RFQ sent', hint: 'Our own RFQs and reminders to suppliers' },
  'supplier-reply': { label: 'Supplier replies', hint: 'Suppliers answering our RFQs' },
  other: { label: 'Other', hint: 'Not an enquiry, not an RFQ, not a reply' },
}

/** The chips, in the order they read best: the work first, then the traffic we generate. */
export const MAIL_KIND_ORDER: MailKind[] = ['to-create', 'auto-created', 'enquiry', 'supplier-reply', 'rfq-sent', 'other']

export function mailKind(
  mail: MailMessage,
  processed: readonly EnquirySummary[],
  /** Enquiries Bosun created without a person (`AUTO_ENQUIRY_MARK` on `enquiry_remarks`), 2026-09-21. */
  autoCreated: ReadonlySet<string> = new Set(),
): MailKind {
  if (isOwnOutgoingRfq(mail.subject)) return 'rfq-sent'
  if (readsAsSupplierReply(mail)) return 'supplier-reply'
  if (mail.folder === 'sent' && (repliedRfqId(mail.subject) !== null || /request for quotation/i.test(mail.subject))) return 'rfq-sent'
  // Automatic and still nobody's: the purchaser's queue. Once any other enquiry is made from the
  // mail — or a person has picked the automatic one up — it is an ordinary "enquiry created".
  if (processed.length > 0 && processed.every((e) => autoCreated.has(e.id) && (e.status === 'Draft' || e.status === 'Open'))) return 'auto-created'
  if (processed.length > 0) return 'enquiry'
  if (mail.folder === 'inbox' || mail.folder === 'flagged') {
    const verdict = effectiveVerdict(mail.id, classifyRfqMail(mail).verdict)
    if (verdict !== 'not-rfq') return 'to-create'
  }
  return 'other'
}
